import { createContext, ReactNode, useEffect, useState } from 'react';
// @mui
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type CollapseDrawerContextProps = {
  collapsibleSidebarEnabled: boolean;
  isCollapse?: boolean;
  collapseClick: boolean;
  collapseHover: boolean;
  onToggleCollapse: VoidFunction;
  onHoverEnter: VoidFunction;
  onHoverLeave: VoidFunction;
};

const initialState: CollapseDrawerContextProps = {
  collapsibleSidebarEnabled: true,
  collapseClick: false,
  collapseHover: false,
  onToggleCollapse: () => {},
  onHoverEnter: () => {},
  onHoverLeave: () => {},
};

const CollapseDrawerContext = createContext(initialState);

type CollapseDrawerProviderProps = {
  children: ReactNode;
};

function CollapseDrawerProvider({ children }: CollapseDrawerProviderProps) {
  // It came through as a string once during testing, so we're treating this as
  // Next being weird sometimes
  const collapsibleSidebarEnabled =
    typeof process.env.COLLAPSIBLE_SIDEBAR === 'boolean'
      ? process.env.COLLAPSIBLE_SIDEBAR
      : process.env.COLLAPSIBLE_SIDEBAR === 'true';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [collapse, setCollapse] = useState({
    click: collapsibleSidebarEnabled,
    hover: false,
  });

  const setCollapseWrapped = (value: any) => {
    if (collapsibleSidebarEnabled) {
      setCollapse(value);
    } else {
      setCollapse({
        click: false,
        hover: false,
      });
    }
  };

  useEffect(() => {
    if (isMobile) {
      setCollapseWrapped({
        click: false,
        hover: false,
      });
    }
  }, [isMobile]);

  const handleToggleCollapse = () => {
    setCollapseWrapped({ ...collapse, click: !collapse.click });
  };

  const handleHoverEnter = () => {
    if (collapse.click) {
      setCollapseWrapped({ ...collapse, hover: true });
    }
  };

  const handleHoverLeave = () => {
    setCollapseWrapped({ ...collapse, hover: false });
  };

  return (
    <CollapseDrawerContext.Provider
      value={{
        collapsibleSidebarEnabled: collapsibleSidebarEnabled,
        isCollapse: collapse.click && !collapse.hover,
        collapseClick: collapse.click,
        collapseHover: collapse.hover,
        onToggleCollapse: handleToggleCollapse,
        onHoverEnter: handleHoverEnter,
        onHoverLeave: handleHoverLeave,
      }}
    >
      {children}
    </CollapseDrawerContext.Provider>
  );
}

export { CollapseDrawerProvider, CollapseDrawerContext };
